import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { fadeUp, stagger } from './motionPresets';

const pricing = [
  {
    name: 'Free', price: '$0', period: '/mo', desc: 'Get started with basic compliance tracking',
    features: ['Up to 2 employees', '2 PAF files', 'Basic compliance tracking', 'Document uploads', 'Community support'],
    cta: 'Get Started Free', popular: false,
  },
  {
    name: 'Starter', price: '$15', period: '/mo', desc: 'For small teams scaling up',
    features: ['Up to 15 employees', '15 PAF files', 'Compliance automation', 'Email support', 'CSV exports', 'Public access links'],
    cta: 'Start Free Trial', popular: false,
  },
  {
    name: 'Professional', price: '$25', period: '/mo', desc: 'For growing companies',
    features: ['Up to 25 employees', '25 PAF files', 'Advanced compliance automation', 'Priority support', 'Audit trail', 'Role-based access', 'Compliance alerts'],
    cta: 'Start Free Trial', popular: true,
  },
  {
    name: 'Enterprise', price: 'Custom', period: '', desc: 'For large organizations with 25+ employees',
    features: ['Unlimited employees', 'Unlimited PAFs', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'SSO & advanced security', 'Multi-company support'],
    cta: 'Contact Sales', popular: false,
  },
];

export default function PricingSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#F8FAFF]" id="pricing">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-16">
          <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Pricing</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">Simple, transparent pricing</h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">Choose the plan that fits your organization. Start with a free account.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6 max-w-6xl mx-auto">
          {pricing.map((plan, i) => (
            <motion.div
              key={plan.name}
              {...stagger}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className={`rounded-2xl p-8 transition-all duration-200 ${plan.popular
                ? 'bg-gradient-to-br from-indigo-600 to-indigo-700 text-white shadow-xl shadow-indigo-500/25 ring-4 ring-indigo-300/20 scale-[1.02]'
                : 'bg-white border border-indigo-100/50 shadow-sm hover:border-indigo-400 hover:shadow-lg'
              }`}
            >
              {plan.popular && (
                <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-4 uppercase tracking-wider">Most Popular</span>
              )}
              <h3 className={`text-lg font-bold mb-1 ${plan.popular ? 'text-white' : 'text-[#0F172A]'}`}>{plan.name}</h3>
              <p className={`text-sm mb-6 ${plan.popular ? 'text-indigo-100' : 'text-[#64748B]'}`}>{plan.desc}</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-[#0F172A]'}`}>{plan.price}</span>
                {plan.period && <span className={`text-sm ${plan.popular ? 'text-indigo-200' : 'text-[#94A3B8]'}`}>{plan.period}</span>}
              </div>
              <Link
                to={plan.name === 'Enterprise' ? '/contact' : '/login'}
                className={`block text-center py-3 rounded-xl font-semibold text-sm transition-all ${plan.popular
                  ? 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-md'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-500/20'
                }`}
              >
                {plan.cta}
              </Link>
              <ul className="mt-8 space-y-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.popular ? 'text-indigo-200' : 'text-indigo-500'}`} strokeWidth={1.5} />
                    <span className={`text-sm ${plan.popular ? 'text-indigo-50' : 'text-[#64748B]'}`}>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
